import { useEffect, useState } from 'react';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { Layout } from './components/layout';
import { RequirePasswordChange } from './components/require-password-change';
import { RequirePerm } from './components/require-perm';
import { CONSIGNMENT_PERMS, STOCKIST_CONSIGNMENT_PERMS } from '@mlm/shared';
import { useAuthStore } from './lib/auth-store';
import { restoreSession } from './lib/api';
import { useTenantTheme } from './lib/theme-boot';
import { ChangePasswordPage } from './pages/change-password';
import { LoginPage } from './pages/login';
import { PosPage } from './pages/pos';
import { CheckoutPage } from './pages/checkout';
import { ReceiptPage } from './pages/receipt';
import { TopupPage } from './pages/topup';
import { ReleasingPage } from './pages/releasing';
import { ReleasingHistoryPage } from './pages/releasing-history';
import { TransactionsPage } from './pages/transactions';
import { OrdersPage } from './pages/orders';
import { InventoryPage } from './pages/inventory';
import { ConsignmentCustomersPage } from './pages/consignment/customers';
import { ConsignOutPage } from './pages/consignment/consign-out';
import { ConsignmentCustomerPage } from './pages/consignment/customer-detail';
import { StockistBranchesPage } from './pages/stockist-consignment/branches';
import { StockistConsignOutPage } from './pages/stockist-consignment/consign-out';
import { StockistBranchDetailPage } from './pages/stockist-consignment/branch-detail';
import { TimeLogsPage } from './pages/time-logs';
import { DailyReportPage } from './pages/daily-report';
import { EndShiftPage } from './pages/end-shift';

/**
 * On every mount (including page refresh), silently re-hydrate the session
 * from the refresh cookie. The auth store is in-memory only, so a reload
 * starts with `token=null`; without this boot step `RequireAuth` would always
 * redirect to /login even when the refresh cookie is still valid.
 */
function useAuthBoot(): 'pending' | 'ready' {
  const [status, setStatus] = useState<'pending' | 'ready'>('pending');
  useEffect(() => {
    let cancelled = false;
    void restoreSession().finally(() => {
      if (!cancelled) setStatus('ready');
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return status;
}

export function App() {
  // Above the auth gate on purpose: /tenant/config is public, so the login
  // screen is already in the tenant's colours.
  useTenantTheme();
  const bootStatus = useAuthBoot();
  if (bootStatus === 'pending') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground text-sm">
        Restoring session…
      </div>
    );
  }
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route element={<RequireAuth />}>
        {/* Reachable while `must_change_password` is set — the gate below
            redirects here, so it must live OUTSIDE the gate. */}
        <Route path="/change-password" element={<ChangePasswordPage />} />
        <Route element={<RequirePasswordChange />}>
          <Route element={<Layout />}>
            {/* Keyed: the two tabs are the same component, and each must mount
                fresh (its own stockist confirmation, its own dialogs). */}
            <Route path="/" element={<PosPage key="retail" />} />
            <Route path="/stockist" element={<PosPage key="stockist" mode="stockist" />} />
            {/* The sale travels between these in a store (lib/cart-store.ts):
                /checkout goes home when there is nothing to check out. */}
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/receipt/:orderId" element={<ReceiptPage />} />
            <Route path="/topup" element={<TopupPage />} />
            <Route path="/releasing" element={<ReleasingPage />} />
            <Route path="/releasing/history" element={<ReleasingHistoryPage />} />
            <Route path="/transactions" element={<TransactionsPage />} />
            <Route path="/orders" element={<OrdersPage />} />
            <Route path="/inventory" element={<InventoryPage />} />
            {/* Accounting v1 — consignment (flag `consignment_enabled`; the
                server re-gates every /pos/consignment* route). */}
            <Route path="/consignment" element={<ConsignmentCustomersPage />} />
            <Route
              path="/consignment/new"
              element={
                <RequirePerm perm={CONSIGNMENT_PERMS.consignOut} label="Consign Out">
                  <ConsignOutPage />
                </RequirePerm>
              }
            />
            <Route path="/consignment/:userId" element={<ConsignmentCustomerPage />} />
            {/* Track 2A — stockist consignment (flag `stockist_consignment_enabled`;
                the server re-gates every /pos/stockist-consignment* route). */}
            <Route path="/stockist-consignment" element={<StockistBranchesPage />} />
            <Route
              path="/stockist-consignment/new"
              element={
                <RequirePerm
                  perm={STOCKIST_CONSIGNMENT_PERMS.consignOut}
                  label="Consign Out to a Stockist"
                >
                  <StockistConsignOutPage />
                </RequirePerm>
              }
            />
            <Route path="/stockist-consignment/:branchId" element={<StockistBranchDetailPage />} />
            <Route path="/time-logs" element={<TimeLogsPage />} />
            <Route path="/daily-report" element={<DailyReportPage />} />
            <Route path="/end-shift" element={<EndShiftPage />} />
          </Route>
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function RequireAuth() {
  const token = useAuthStore((s) => s.token);
  if (!token) return <Navigate to="/login" replace />;
  return <Outlet />;
}
